import React from 'react';
import { IFaq } from 'ui/components/molecules/FaqAccordion';
import SocialMediaLinksSection from 'ui/components/organism/SocialMediaLinksSection';
import BreadCrumbs from 'ui/components/molecules/Breadcrumbs';
import InfoSection from 'ui/components/organism/InfoSection';
import ErrorScreen from 'ui/components/molecules/ErrorScreen';
import AccessoriesDetailSection from 'ui/components/organism/AccessoriesDetailSection';
import { ICouponDiscount } from 'ui/interface/product.interfaces';
import { IAccessoriesDetail } from 'ui/interface/accessories.interface';
import dynamic from 'next/dynamic';
import AccessoriesFloatingAction from 'ui/components/organism/AccessoriesFloatingAction';
import FaqAccordionNoscript from 'ui/components/molecules/FaqAccordionNoScript';

const FaqAccordion = dynamic(() => import('ui/components/molecules/FaqAccordion'), { ssr: false });

interface Props {
  errorCode?: number;
  breadcrumbItems?: any;
  faqs: {
    title: string;
    faqList: IFaq[];
  };
  termAndCondition: any;
  isCMS: boolean;
  serviceDiscounts: ICouponDiscount[];
  accessoriesDetail: IAccessoriesDetail;
  minimumDeliveryFee: {
    value: string;
  };
  quantity: number;
  setQuantity?: (val: number) => void;
  handleSubmit?: (val: 'CART' | 'BUY') => void;
}

function AccessoriesDetailPageTemplate({
  errorCode,
  breadcrumbItems,
  faqs,
  termAndCondition,
  isCMS = false,
  serviceDiscounts,
  minimumDeliveryFee,
  accessoriesDetail,
  quantity,
  setQuantity,
  handleSubmit,
}: Props) {
  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

  const contentBottomInformation = {
    title: accessoriesDetail?.titleBottomInformation,
    desc: accessoriesDetail?.descriptionBottomInformation,
  };

  const serviceType = accessoriesDetail?.isTIntouch ? 'tintouch' : 'accessories';

  // Error page
  if (errorCode) {
    return <ErrorScreen errorCode={errorCode} />;
  }

  return (
    <React.Fragment>
      <BreadCrumbs
        isCMS={isCMS}
        isMobileScreen={false}
        items={breadcrumbItems}
        position="md:top-[105px] lg:top-[108px] xl:top-[124px] px-5"
        isUsingDefaultAnchor
      />

      <AccessoriesDetailSection
        isCMS={isCMS}
        termAndCondition={termAndCondition}
        serviceDiscounts={serviceDiscounts}
        minimumDeliveryFee={minimumDeliveryFee}
        accessoriesDetail={accessoriesDetail}
        quantity={quantity}
        setQuantity={setQuantity}
        serviceType={serviceType}
        emarsysData={{
          pageTitle: accessoriesDetail?.contentTitle || '',
          breadcrumbItems,
          sku: accessoriesDetail?.accessoriesCode || '',
          skuPrice: (
            accessoriesDetail?.strikeThroughPrice?.value ||
            accessoriesDetail?.price?.value ||
            0
          ).toString(),
          carModel: accessoriesDetail?.accessoriesCarModels,
        }}
      />

      <InfoSection
        info={contentBottomInformation}
        sectionClassName={'!px-0 mt-4 mb-0 text-reliableBlack lg:mt-8 lg:mb-8 lg:!px-3'}
        headingClassName="text-sm !mb-1.5 lg:text-xl"
        contentClassName="text-xs lg:text-base"
        impressionData={{
          breadcrumbItems,
          pageTitle: accessoriesDetail?.contentTitle || '',
          sku: accessoriesDetail?.accessoriesCode || '',
          skuPrice: (
            accessoriesDetail?.strikeThroughPrice?.value ||
            accessoriesDetail?.price?.value ||
            0
          ).toString(),
          carModels: accessoriesDetail?.accessoriesCarModels,
        }}
        feature="ACCESSORIES_INFO"
        idName="accessories-and-spareparts-toyota"
      />
      {faqs?.faqList.length > 0 && (
        <>
          <FaqAccordionNoscript title={accessoriesDetail?.faqTitle} faqs={faqs?.faqList} />
          <FaqAccordion
            title={accessoriesDetail?.faqTitle}
            faqs={faqs?.faqList}
            isCMS={isCMS}
            containerClassname="py-2 mb-[22px] lg:mb-[30px] lg:px-3 lg:py-0"
            headingClassName="!text-base lg:!text-xl mb-[2px] lg:mb-2"
            impressionData={{
              breadcrumbItems,
              pageTitle: accessoriesDetail?.contentTitle || '',
              sku: accessoriesDetail?.accessoriesCode || '',
              skuPrice: (
                accessoriesDetail?.strikeThroughPrice?.value ||
                accessoriesDetail?.price?.value ||
                0
              ).toString(),
              carModels: accessoriesDetail?.accessoriesCarModels,
            }}
            feature="ACCESSORIES_INFO"
          />
        </>
      )}

      <div className="flex flex-col gap-[14px]">
        <SocialMediaLinksSection
          enableSharing={isCMS}
          shareUrl={`${PUBLIC_URL}`}
          sectionClassName="!mt-0"
          feature="ACCESSORIES"
          impressionData={{
            pageTitle: accessoriesDetail?.contentTitle || '',
            breadCrumb: breadcrumbItems,
            sku: accessoriesDetail?.accessoriesCode || '',
            skuPrice: (
              accessoriesDetail?.strikeThroughPrice?.value ||
              accessoriesDetail?.price?.value ||
              0
            ).toString(),
            carModel: accessoriesDetail?.accessoriesCarModels,
          }}
        />

        <BreadCrumbs isMobileScreen={true} items={breadcrumbItems} isUsingDefaultAnchor />
      </div>

      <AccessoriesFloatingAction
        showCounter={true}
        accessoriesDetail={accessoriesDetail}
        serviceType={serviceType}
        quantity={quantity}
        setQuantity={setQuantity}
        handleSubmit={handleSubmit}
        isCMS={isCMS}
        emarsysData={{
          pageTitle: accessoriesDetail?.contentTitle || '',
          breadcrumbItems,
          sku: accessoriesDetail?.accessoriesCode || '',
          skuPrice: (
            accessoriesDetail?.strikeThroughPrice?.value ||
            accessoriesDetail?.price?.value ||
            0
          ).toString(),
          carModel: accessoriesDetail?.accessoriesCarModels,
        }}
      />
    </React.Fragment>
  );
}

export default AccessoriesDetailPageTemplate;
